import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import * as adminApi from "@/api/admin";
import type { QrRedirect, RedirectClick } from "@/api/admin";
import { CAMPAIGNS, backendPath } from "@/pages/qr-redirect/redirects";

type Tab = "history" | "status";

// Every campaign path in the frontend registry. `path` is the backend's
// canonical key (last segment); `redirect_from` is the full site path.
// Synced on load so new redirects.ts entries get a toggle automatically.
const REGISTRY_ITEMS = CAMPAIGNS.flatMap(c =>
  c.paths.map(p => ({
    path: backendPath(p),
    redirect_from: p,
  })),
);
const REGISTRY_PATHS = new Set(REGISTRY_ITEMS.map(i => i.path));
// Backend key -> full site path, e.g. "coupons" -> "/app/padu/for-you/coupons".
// Existing backend rows keep the redirect_from they were created with (sync
// never updates), so display resolves through the registry instead.
const FULL_PATH_BY_KEY = new Map(REGISTRY_ITEMS.map(i => [i.path, i.redirect_from]));

function formatDate(iso: string) {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function ToggleSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? "bg-[#00d9d9]" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function LoadingRows() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
    </div>
  );
}

function HistoryTab() {
  const [clicks, setClicks] = useState<RedirectClick[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    adminApi.getRedirectClicks()
      .then(setClicks)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load redirect clicks.</AlertDescription>
      </Alert>
    );
  }
  if (loading) return <LoadingRows />;
  if (clicks.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">No redirect clicks yet.</p>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left">
              <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Redirect From</th>
              <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
              <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">App</th>
              <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">User Agent</th>
            </tr>
          </thead>
          <tbody>
            {clicks.map(c => (
              <tr key={c.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                <td className="px-4 py-2.5 text-gray-800 whitespace-nowrap">{formatDate(c.date)}</td>
                <td className="px-4 py-2.5 font-medium text-gray-800 whitespace-nowrap">{c.redirect_from}</td>
                <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{c.campaign_id || "—"}</td>
                <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{c.app}</td>
                <td className="px-4 py-2.5 text-gray-400 max-w-[320px] truncate" title={c.user_agent}>
                  {c.user_agent}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusTab() {
  const [redirects, setRedirects] = useState<QrRedirect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    adminApi.syncQrRedirects(REGISTRY_ITEMS)
      .then(setRedirects)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (r: QrRedirect) => {
    setToggleError(null);
    setTogglingId(r.id);
    const next = !r.active;
    setRedirects(prev => prev.map(x => x.id === r.id ? { ...x, active: next } : x));
    try {
      const updated = await adminApi.updateQrRedirect(r.id, { active: next });
      setRedirects(prev => prev.map(x => x.id === r.id ? updated : x));
    } catch {
      setRedirects(prev => prev.map(x => x.id === r.id ? { ...x, active: r.active } : x));
      setToggleError(`Failed to update "${r.path}".`);
    } finally {
      setTogglingId(null);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load redirects.</AlertDescription>
      </Alert>
    );
  }
  if (loading) return <LoadingRows />;
  if (redirects.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">No redirects yet.</p>;
  }

  return (
    <div className="space-y-4">
      {toggleError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{toggleError}</AlertDescription>
        </Alert>
      )}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Path</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Redirect From</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Updated</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Active</th>
              </tr>
            </thead>
            <tbody>
              {redirects.map(r => (
                <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 font-medium text-gray-800 whitespace-nowrap">
                    <span className={REGISTRY_PATHS.has(r.path) ? "" : "text-gray-400"}>{r.path}</span>
                    {!REGISTRY_PATHS.has(r.path) && (
                      <span
                        className="ml-2 text-[9px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wide text-amber-600 bg-amber-50 border-amber-200"
                        title="No campaign in redirects.ts uses this path"
                      >
                        Not in registry
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                    {FULL_PATH_BY_KEY.get(r.path) ?? r.redirect_from}
                  </td>
                  <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">{formatDate(r.updated_at)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch
                        checked={r.active}
                        disabled={togglingId === r.id}
                        onChange={() => handleToggle(r)}
                      />
                      {togglingId === r.id && (
                        <Loader2 size={12} className="animate-spin text-gray-400" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function RedirectsView() {
  const [tab, setTab] = useState<Tab>("history");

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
        {([
          { key: "history", label: "History" },
          { key: "status", label: "Active Status" },
        ] as { key: Tab; label: string }[]).map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
              tab === t.key
                ? "bg-white text-gray-900 font-medium shadow-sm border border-gray-200"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "history" ? <HistoryTab /> : <StatusTab />}
    </div>
  );
}
